import { useState, useCallback } from "react";
import { TabValue } from "./Tab";

interface UseTabSingleSelectNonNullableProps<T extends TabValue> {
  initialTab: T;
  onTabSelect?: (tabValue: T) => void;
}

interface UseTabSingleSelectNullableProps<T extends TabValue> {
  initialTab?: T | null;
  onTabSelect?: (tabValue: T) => void;
}

type UseTabSingleSelect = {
  <T extends TabValue>(
    props: UseTabSingleSelectNonNullableProps<T>,
  ): {
    activeTab: T;
    onTabSelect: (tabValue: T) => void;
  };
  <T extends TabValue>(
    props: UseTabSingleSelectNullableProps<T>,
  ): {
    activeTab: T | null;
    onTabSelect: (tabValue: T) => void;
  };
};

export const useTabSingleSelect: UseTabSingleSelect = <T extends TabValue>({
  initialTab = null,
  onTabSelect,
}:
  | UseTabSingleSelectNonNullableProps<T>
  | UseTabSingleSelectNullableProps<T>) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabSelect = useCallback(
    (tabValue: T) => {
      setActiveTab(tabValue);
      if (onTabSelect) {
        onTabSelect(tabValue);
      }
    },
    [onTabSelect],
  );

  return {
    activeTab,
    onTabSelect: handleTabSelect,
  };
};

interface UseTabMultiSelectProps<T extends TabValue> {
  initialTabs?: T[] | null;
  afterTabSelect?: (tabValues: T[]) => void;
}

export const useTabMultiSelect = <T extends TabValue>({
  initialTabs = null,
  afterTabSelect,
}: UseTabMultiSelectProps<T> = {}) => {
  const [activeTabs, setActiveTabs] = useState<T[]>(initialTabs ?? []);

  const handleTabSelect = useCallback(
    (tabValue: T) => {
      const newActiveTabs = activeTabs.includes(tabValue)
        ? activeTabs.filter((tab) => tab !== tabValue)
        : [...activeTabs, tabValue];

      setActiveTabs(newActiveTabs);
      if (afterTabSelect) {
        afterTabSelect(newActiveTabs);
      }
    },
    [activeTabs, afterTabSelect],
  );

  return {
    activeTabs,
    onTabSelect: handleTabSelect,
  };
};
